import { AssetStatus, AssetType } from "@amzn/ads-magneto-typescript-client";
import type { GeneratedInsight } from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    InsightOutputFormat,
    InsightType,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useAsync } from "@react-hookz/web";
import { isEmpty, isUndefined } from "lodash";
import pWaitFor from "p-wait-for";
import React, { useCallback, useEffect } from "react";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../../../../api/cdbApi/cdbApi";
import { getMagnetoApi } from "../../../../../api/magnetoApi/magnetoApi";
import { magnetoS3LocationToDownloadAsset } from "../../../../../hooks/generatedInsights/useGeneratedInsightsAction";
import { useInsightIdentifierEffect } from "../../../../../hooks/misc/useInsightIdentifierEffect";
import { useAsyncErrorStatusNotification } from "../../../../../hooks/notifications/useAsyncErrorStatusNotification";
import { authStore } from "../../../../../store/auth";
import { FeatureFlagsStore } from "../../../../../store/featureFlags";
import { discoveryFilterStore } from "../../../../../store/filters";
import { globalWorkbenchStore } from "../../../../../store/globalWorkbench";
import { insightDetailsStore } from "../../../../../store/insightDetails";
import { reportIssueStore } from "../../../../../store/reportIssue";
import { workbenchStore } from "../../../../../store/workbench";
import { AquilaThumbsFeedbackPlacement } from "../../../../../types/aquila";
import type { CardDetail } from "../../../../../types/card";
import type { DownloadAsset } from "../../../../../types/common";
import { InsightPreviewFileFormats } from "../../../../../types/insightPreviewFileFormats";
import { Progress } from "../../../../../types/progress";
import { isLoading } from "../../../../../utils/api.utils";
import { logViewInsightDetailsClick } from "../../../../../utils/telemetry.utils";
import { AquilaThumbsFeedback } from "../../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { StyledActionsContainer } from "../../../commonStyles";
import { InsightSelectionActionText } from "../../InsightSelectionActionText";
import { VisualizationSuccessAction } from "../VisualizationSuccessAction";
import { WbVisualizationFailedAction } from "../WbVisualizationFailedAction";

export interface CuratedInsightCardActionProps {
    executionId: string;
    advertiserId: string;
    title: string;
    details: CardDetail[];
    progress: Progress;
    description?: string;
    index?: number;
    outputFileFormats?: string[];
    insight?: GeneratedInsight;
}

export const CuratedInsightCardAction: React.FunctionComponent<
    CuratedInsightCardActionProps
> = ({
    executionId,
    advertiserId,
    progress,
    title,
    details,
    description,
    index,
    outputFileFormats = [],
    insight = {} as GeneratedInsight,
}) => {
    const [setInsightDetails, setAsyncStatus] = useStore(
        insightDetailsStore,
        (state) => [state.setInsightDetails, state.setAsyncStatus],
    );
    const [setReportIssueContextData] = useStore(reportIssueStore, (state) => [
        state.setReportIssueContextData,
    ]);
    const [contentSubType, filters] = useStore(
        discoveryFilterStore,
        (state) => [state.getContentSubType(), state.getFilters()],
    );
    const [useUnifiedSearch, thumbsFeedbackCard, globalWorkbench] = useStore(
        FeatureFlagsStore,
        (state) => [
            state.unifiedSearch,
            state.thumbsFeedbackCard,
            state.globalWorkbench,
        ],
    );

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);
    const insightsData = filters[contentSubType.provider].data;

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [requesterAlias] = useStore(authStore, (state) => [state.username]);

    const [previewAsset, setPreviewAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);
    const [downloadAsset, setDownloadAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);

    const disabled = Object.values({
        executionId,
        advertiserId,
    }).some((val) => isEmpty(val));

    const isGlobalWorkbench = useStore(
        FeatureFlagsStore,
        (state) => state.globalWorkbench,
    );

    const [downloadState, downloadAction] = useAsync(async () => {
        if (isGlobalWorkbench) {
            const { data } = await (
                await getMagnetoApi()
            ).getDeckAsset({
                deckId: executionId,
                workbenchScope: advertiserId,
                assetType: AssetType.PPTX,
            });
            const pptx = magnetoS3LocationToDownloadAsset(data);
            setDownloadAsset(pptx);
        } else {
            const outputFormat = outputFileFormats.includes("zip")
                ? InsightOutputFormat.ZIP
                : InsightOutputFormat.PPTX;
            const data = await (
                await getCdbApiClient()
            ).getGeneratedInsightDownload({
                requesterAlias,
                executionId,
                advertiserId,
                outputFormat,
            });
            setDownloadAsset(data as DownloadAsset);
        }
    });
    useAsyncErrorStatusNotification(downloadState);
    useEffect(() => {
        setAsyncStatus(downloadState.status);
    }, [downloadState, setAsyncStatus]);

    const [previewState, previewAction] = useAsync(async () => {
        if (isGlobalWorkbench) {
            await pWaitFor(
                async () => {
                    const { data } = await (
                        await getMagnetoApi()
                    ).getDeckAsset({
                        deckId: executionId,
                        workbenchScope: advertiserId,
                        assetType: AssetType.PDF,
                    });
                    if (data && data.status === AssetStatus.SUCCESS) {
                        const pdfDownload =
                            magnetoS3LocationToDownloadAsset(data);
                        setPreviewAsset(pdfDownload);
                        return true;
                    } else if (data && data.status === AssetStatus.ERROR) {
                        throw new Error("preview failed to generate");
                    } else {
                        return false;
                    }
                },
                {
                    interval: 1000,
                    timeout: 300000,
                },
            );
        } else {
            const data = await (
                await getCdbApiClient()
            ).getGeneratedInsightDownload({
                requesterAlias,
                executionId,
                advertiserId,
                outputFormat: InsightOutputFormat.PDF,
            });
            setPreviewAsset(data as DownloadAsset);
        }
    });
    useAsyncErrorStatusNotification(previewState);

    const handleInsightDetails = useCallback(async (): Promise<void> => {
        logViewInsightDetailsClick(
            executionId,
            (index ?? 0) + 1,
            insightsData.length,
            undefined,
            useUnifiedSearch,
        );
        void previewAction.execute();
        void downloadAction.execute();
        setReportIssueContextData({ ...insight });
    }, [insightsData, executionId, index, insight]);

    useInsightIdentifierEffect(executionId, handleInsightDetails);

    React.useEffect(() => {
        if (!isUndefined(downloadAsset)) {
            setInsightDetails({
                open: true,
                info: {
                    title,
                    details,
                    previewFileFormat: InsightPreviewFileFormats.PDF,
                    outputFileFormats,
                    insightType: InsightType.WB,
                    identifier: executionId,
                    previewAsset,
                    downloadAsset,
                    description,
                    previewLoading: isLoading(previewState.status),
                },
            });
        }
    }, [previewAsset, downloadAsset, previewState.status]);
    const numSelectedSlides = globalWorkbench
        ? getSlidesSelected(executionId).length
        : getSlideIndexes(executionId).size;

    return (
        <StyledActionsContainer>
            {(() => {
                switch (progress) {
                    case Progress.VISUALIZATION_FAILED:
                        return (
                            <WbVisualizationFailedAction
                                executionId={executionId}
                            />
                        );
                    case Progress.VISUALIZATION_WARNING:
                        return (
                            <VisualizationSuccessAction
                                executionId={executionId}
                                disabled={disabled}
                                handleInsightDetails={handleInsightDetails}
                                warning
                            />
                        );
                    default:
                        return (
                            <VisualizationSuccessAction
                                executionId={executionId}
                                disabled={disabled}
                                handleInsightDetails={handleInsightDetails}
                            />
                        );
                }
            })()}
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={executionId}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && thumbsFeedbackCard && (
                <AquilaThumbsFeedback
                    identifier={executionId}
                    insightType={InsightType.WB}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                />
            )}
        </StyledActionsContainer>
    );
};
